import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';
import { Router } from 'react-router';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import classnames from 'classnames';

import { Page, EventBusBridge, Masthead, TrackingWrapper } from '@piggybank/core';
import { Wizard, WizardPage } from '@piggybank/form';

import CardDetails from '~/pages/CardDetails';
import CardSuccess from '~/pages/CardSuccess';
import ErrorPage from '~/pages/ErrorPage';
import Loading from '~/pages/Loading';
import Timeout from '~/pages/Timeout';
import TimeoutDialog from '~/pages/TimeoutDialog';
import { extendSaasToken, logOut, submitCreditCardApplication } from '~/apis';
import { isMobile } from '~/utils';
import routes from '~/data/routes';
import EndUserMonitoring from '~/components/EndUserMonitoring';

import styles from './App.module.css';

import nls from '~/nls/App';
import { createTranslator } from '~/lib/nls-helper';

import getEnvironment from '~/lib/environment';

import getSpecs from '~/lib/tealium/spec';
import { link, view } from '~/lib/tealium/actions';

export const AppContext = React.createContext();

const environment = getEnvironment();

const track = event => {
  if (environment.tealium.isEnabled && event) {
    const { content } = event;
    const isViewEvent = !!get(event, 'rule.isViewEvent');
    isViewEvent? view(content): link(content);
  }
};

const NLS = createTranslator(nls);

const setPageTitle = () => {
  document.title = NLS`Credit Card Offer`;
};

const wait = duration => new Promise(resolve => setTimeout(resolve, duration));

//in seconds
const SESSION_DURATION = environment?.session?.timeoutAfterMinutes * 60 || 600;
const SHOW_TIMEOUT_DIALOG_ON = environment?.session?.timeoutWarningAfterMinutes * 60 || 540;
const TIMEOUT_DURATION = SESSION_DURATION - SHOW_TIMEOUT_DIALOG_ON;

class App extends Component {

  static propTypes = {
    cardOfferData: PropTypes.objectOf(
      PropTypes.object,
    ),
    globalData: PropTypes.objectOf(PropTypes.string),
    history: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.object,
    ]),
  };
  state = {
    currentPage: isEmpty(this.props.cardOfferData)? routes['error'].pageName: routes['card-details'].pageName,
    loading: false,
    initialData: {
      globalData: this.props.globalData,
      offer: this.props.cardOfferData,
    },
    isMobile: isMobile(get(this.props.globalData, 'ismobile')),
    submittedData: {},
    timeoutCountDown: TIMEOUT_DURATION,
  };

  componentDidMount () {
    setPageTitle();
    this.restartSessionTimer();
    if (!isEmpty(this.props.cardOfferData)) this.props.history.push((routes['card-details'].uri + this.props.history.location.search));
  }

  componentWillUnmount () {
    clearTimeout(this.sessionTimer);
  }

  restartSessionTimer = () => {
    if (!this.state.isMobile) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = setTimeout(this.timeOutTick, SHOW_TIMEOUT_DIALOG_ON * 1000);
      this.setState({ timeoutCountDown: TIMEOUT_DURATION });
    }
  };

  timeOutTick = () => {
    if (this.state.timeoutCountDown <= 0) {
      logOut().finally(() => {
        this.props.history.push((routes['timeout'].uri + this.props.history.location.search));
        this.setState({ currentPage: routes['timeout'].pageName });
      });
      return;
    }

    if (this.state.currentPage !== routes['error'].pageName) {
      this.setState(state => ({ timeoutCountDown: state.timeoutCountDown - 1 }));
      this.sessionTimer = setTimeout(this.timeOutTick, 1000);
    } else {
      this.setState({ timeoutCountDown: 0 });
    }
  };

  extendSession = async () => {
    try {
      await extendSaasToken({
        globalData: this.props.globalData,
      });
      this.restartSessionTimer();
    } catch (err) {
      this.props.history.push(routes['timeout'].uri + this.props.history.location.search);
      this.setState({ currentPage: routes['timeout'].pageName });
    }
  };

  handleSubmit = async () => {
    try {
      this.setState({ loading: true });
      await wait(2000);
      let response = await submitCreditCardApplication({
        requestData: {
          cardOffer: this.props.cardOfferData,
        },
        globalData: this.props.globalData,
      });
      this.setState({
        submittedData: {
          creditLimit: get(this.props.cardOfferData, 'creditCardOffer.creditLimit'),
          folio: get(response, 'folio'),
        },
      });
      if (response.responseCode === 'SUCCESS') {
        this.props.history.push(routes['card-success'].uri + this.props.history.location.search);
        this.setState({ currentPage: routes['card-success'].pageName });
      } else {
        this.props.history.push(routes['error'].uri + this.props.history.location.search);
        this.setState({ currentPage: routes['error'].pageName });
      }
      this.setState({ loading: false });
    } catch (err) {
      this.props.history.push(routes['error'].uri + this.props.history.location.search);
      this.setState({ currentPage: routes['error'].pageName });
      this.setState({ loading: false });
    }
  }

  renderCardsJourney (classes) {
    if (this.state.loading) {
      return (
        <div className={classes}>
          <Loading show />
        </div>
      );
    } else {
      return (
        <div className={classes}>
          <Router basename="/" history={this.props.history}>
            <Switch>
              <Route exact path={routes['card-details'].uri}>
                {({ history }) => (
                  <div className={classnames(styles.marginTop4)}>
                    <Wizard history={history} onSubmit={this.handleSubmit}>
                      <WizardPage component={CardDetails} />
                    </Wizard>
                  </div>
                )}
              </Route>
              <Route path={routes['card-success'].uri}>
                {({ history }) => (
                  <div className={classnames(styles.marginTop4)}>
                    <Wizard history={history} >
                      <WizardPage component={CardSuccess} />
                    </Wizard>
                  </div>
                )}
              </Route>
              <Route path={routes['timeout'].uri}>
                {({ history }) => (
                  <div className={classnames(styles.marginTop4)}>
                    <Wizard history={history} >
                      <WizardPage component={Timeout} />
                    </Wizard>
                  </div>
                )}
              </Route>
              <Route>
                <div className={classnames(styles.marginTop4)}>
                  <ErrorPage globalData={this.props.globalData} />
                </div>
              </Route>
            </Switch>
          </Router>
          <TimeoutDialog
            extendSession={this.extendSession}
            show={this.state.timeoutCountDown < TIMEOUT_DURATION && this.state.timeoutCountDown > 0}
            timeoutCountDown={this.state.timeoutCountDown}
          />
        </div>
      );
    }
  }

  renderDesktopJourney (classes) {
    return (
      <Page headerSlot={<Masthead marginBottom={0} />}>
        { this.renderCardsJourney(classes) }
      </Page>
    );
  }

  renderMobileJourney (classes) {
    const Empty = () => (
      <div />
    );
    return (
      <Page footerSlot={<Empty />} headerSlot={<Empty />}>
        { this.renderCardsJourney(classes) }
      </Page>
    );
  }

  render () {
    return (
      <>
        <EndUserMonitoring
          appDynamicsApplicationKey={get(environment, 'appDynamics.applicationKey')}
          enabled={get(environment, 'appDynamics.isEnabled')}
        />
        <TrackingWrapper
          onTrackingEvent={events => events.map(e => track(e))}
          specs={getSpecs({
            lang: get(this.props.globalData, 'locale'),
            offerId: get(this.props.globalData, 'offerId'),
            folio: get(this.state.submittedData, 'folio'),
          })}
        >
          <EventBusBridge>
            <AppContext.Provider value={this.state}>
              {isMobile(get(this.props.globalData, 'ismobile'))?
                this.renderMobileJourney(classnames(styles.mobile)):
                this.renderDesktopJourney(classnames(null))}
            </AppContext.Provider>
          </EventBusBridge>
        </TrackingWrapper>
      </>
    );
  }
}

export default App;
